import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchLearnInDevices, fetchDeviceTypes, fetchPalcareDeviceTypes,
  publishLearnIn, pollLearnInDevices,
} from '../../pages/devices/api/devicesApi.js';
import Drawer from './Drawer.jsx';
import './easy-learn-in.css';

/**
 * EasyAddDeviceDrawer — simplified "add a device here" flow, shared by the
 * resident drawer (ResidentsPage) and the area drawer (AreasPage).
 *
 * Solves the workflow complaint that adding a brand-new device required leaving
 * the resident/area, adding it unassigned on the Devices page, then coming back
 * to assign it. Here the device is created AND assigned in one step, without
 * ever losing context.
 *
 * Three steps:
 *   1. Pick the device — curated shortlist from att_easy_learn_in, scoped by
 *      mode (resident-capable vs area-capable). First few render as images, the
 *      rest as a compact list. One pick supplies manufacturer, type, category
 *      and model, replacing five dropdowns in the advanced flow.
 *   2. Identify it — type the serial, or listen for it. Picking the device FIRST
 *      is what lets the listener filter to that product type, so a pendant
 *      learn-in isn't drowned out by door sensors and repeaters.
 *   3. Confirm — names the target before writing, since a mis-assigned device is
 *      a care-safety problem, not just bad data.
 *
 * A serial already in the system is not an error: the flow switches to offering
 * assignment of that existing device, so the user never has to know whether it
 * was added before.
 *
 * Behavior options (device_use_id) are resolved, not offered as a choice: no
 * device in the curated learn-in set has more than one, so the id comes from the
 * curated row or the device type. A device with a genuine choice to make belongs
 * in the advanced AddDeviceDrawer, which also remains for bulk and unassigned
 * adds.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   mode: 'resident' | 'area',      // scopes the catalog and the payload
 *   target: object,                 // resident (resident_id, name, area) or area (area_id, area_name)
 *   existingDevices: Array,         // all campus devices, for duplicate detection
 *   campusId: number,
 *   sessionId: string,
 *   classPrefix?: string,           // page shell prefix: 'rp' (residents) | 'ap' (areas)
 *   roleWeight?: number,            // caller's role weight; gates the advanced-setup hint
 *                                   // (defaults to the most restrictive treatment)
 *   onAdd: (formData: object) => Promise<[boolean, string?]>,
 *   onAssignExisting: (device: object) => Promise<[boolean, string?]>,
 * }} props
 */

const PALCARE_ID = 4;

/** Inovonics transmitter serials are integers in this inclusive range. */
const SERIAL_MIN = 1;
const SERIAL_MAX = 16777216;

/** How many curated devices get the large image treatment before the list. */
const TILE_COUNT = 6;

const LISTEN_SECONDS = 60;

/** Above this role weight the advanced Devices-page flow is worth suggesting;
 *  at or below it, the user can't reasonably drive it themselves. */
const ADVANCED_SETUP_MIN_ROLE = 30;

function validateSerial(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return 'Enter the serial number.';
  if (!/^\d+$/.test(trimmed)) return 'Serial numbers are digits only.';
  const n = parseInt(trimmed, 10);
  if (n < SERIAL_MIN || n > SERIAL_MAX) {
    return `Serial must be between ${SERIAL_MIN} and ${SERIAL_MAX.toLocaleString()}.`;
  }
  return '';
}

/** Serials are stored as strings but compared numerically to dodge "007" vs "7". */
function findExistingDevice(devices, serial) {
  const trimmed = String(serial || '').trim();
  if (!trimmed) return null;
  const asNum = Number(trimmed);
  return (devices || []).find((d) => {
    const uuid = d?.manufacture_uuid;
    if (uuid == null) return false;
    const s = String(uuid).trim();
    if (s === trimmed) return true;
    return Number.isFinite(asNum) && Number(s) === asNum;
  }) || null;
}

const EasyAddDeviceDrawer = ({
  isOpen, onClose, mode = 'resident', target, existingDevices, campusId, sessionId,
  classPrefix = 'rp', roleWeight = 0, onAdd, onAssignExisting,
}) => {
  const p = classPrefix;
  const isAreaMode = mode === 'area';

  const [step, setStep] = useState('device');   // 'device' | 'serial'
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  const [serial, setSerial] = useState('');
  const [serialError, setSerialError] = useState('');
  // Optional free-text placement, area mode only. Residents don't get one:
  // the device belongs to the person, not a spot in the room.
  const [descriptions, setDescriptions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Device types carry the nested models that hold product_type_id /
  // message_class — needed both for the listener and the saved model id.
  const [types, setTypes] = useState([]);

  // Listen-in
  const [listening, setListening] = useState(false);
  const [listenCount, setListenCount] = useState(0);
  const [heard, setHeard] = useState([]);

  const resetAll = useCallback(() => {
    setStep('device');
    setPicked(null);
    setSerial('');
    setSerialError('');
    setDescriptions('');
    setError('');
    setSaving(false);
    setListening(false);
    setListenCount(0);
    setHeard([]);
  }, []);

  // Load the curated shortlist on open, scoped to where it can be installed
  useEffect(() => {
    if (!isOpen) return;
    resetAll();
    setCatalogLoading(true);
    fetchLearnInDevices(isAreaMode ? { isArea: 1, isResident: 0 } : { isResident: 1, isArea: 0 })
      .then((rows) => setCatalog(Array.isArray(rows) ? rows : []))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, [isOpen, isAreaMode, resetAll]);

  // Escape stops an active listen first; the Drawer shell closes the drawer
  useEffect(() => {
    if (!isOpen || !listening) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setListening(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, listening]);

  // Load the type list for the picked device's manufacturer
  useEffect(() => {
    if (!picked) return;
    const fetcher = picked.manufacture_id === PALCARE_ID
      ? fetchPalcareDeviceTypes(campusId)
      : fetchDeviceTypes(campusId);
    fetcher.then((data) => setTypes(data || [])).catch(() => setTypes([]));
  }, [picked, campusId]);

  /** The device type behind the picked row — carries both models and uses. */
  const matchedType = useMemo(() => {
    if (!picked) return null;
    const isPal = picked.manufacture_id === PALCARE_ID;
    return (types || []).find((t) => String(isPal ? t.device_type_id : t.inovonics_type_id)
      === String(picked.device_type_id)) || null;
  }, [picked, types]);

  /** The picked device's model — supplies model id + listen parameters. */
  const model = useMemo(() => {
    if (!picked) return null;
    const models = matchedType?.models || [];
    // Prefer the curated row's model when the procedure supplies one.
    if (picked.manufacture_model_id) {
      const exact = models.find((m) => String(m.model_id) === String(picked.manufacture_model_id));
      if (exact) return exact;
      return { model_id: picked.manufacture_model_id, product_type_id: 0, message_class: 0 };
    }
    return models[0] || null;
  }, [picked, matchedType]);

  const isPalCare = picked?.manufacture_id === PALCARE_ID;

  /**
   * Alarm behavior. Even Inovonics types with a single behavior carry a real
   * inovonics_device_use_id on their `option_number: 0` use, so saving a literal
   * 0 is only correct for PalCare — everything else needs a resolved id.
   *
   * Curated value first (lets a row pin one specific behavior), otherwise the
   * type's first use, matching how the advanced drawer auto-selects allUses[0].
   */
  const deviceUseId = useMemo(() => {
    if (!picked || isPalCare) return 0;
    const curated = parseInt(picked.device_use_id, 10);
    if (Number.isFinite(curated) && curated > 0) return curated;
    const first = (matchedType?.uses || [])[0];
    const derived = parseInt(first?.inovonics_device_use_id, 10);
    return Number.isFinite(derived) ? derived : 0;
  }, [picked, isPalCare, matchedType]);
  // PalCare devices are not learned in over the Inovonics radio.
  const canListen = !!picked && !isPalCare && !!sessionId && !!model?.product_type_id;

  // Listen countdown + poll (same cadence as the advanced drawer)
  useEffect(() => {
    if (!listening) return undefined;
    const interval = setInterval(() => {
      setListenCount((prev) => {
        const next = prev + 1;
        if (next >= LISTEN_SECONDS) {
          setListening(false);
          return LISTEN_SECONDS;
        }
        if (next % 4 === 0) {
          pollLearnInDevices({ campusId, clientId: sessionId })
            .then((data) => setHeard(data || []))
            .catch(() => {});
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [listening, campusId, sessionId]);

  const startListen = useCallback(() => {
    if (!canListen) return;
    setHeard([]);
    setListenCount(0);
    setListening(true);
    // Always the specific product code — we know exactly which device was
    // picked, which is the whole point of choosing it first.
    publishLearnIn({
      campusId,
      clientId: sessionId,
      productType: [model.product_type_id],
      msgClass: model.message_class,
    }).then(([ok]) => { if (!ok) { setListening(false); setError('Could not start listening.'); } });
  }, [canListen, campusId, sessionId, model]);

  const { tiles, rest } = useMemo(() => {
    const sorted = [...(catalog || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return { tiles: sorted.slice(0, TILE_COUNT), rest: sorted.slice(TILE_COUNT) };
  }, [catalog]);

  const duplicate = useMemo(
    () => findExistingDevice(existingDevices, serial),
    [existingDevices, serial],
  );

  const handlePick = useCallback((row) => {
    setPicked(row);
    setStep('serial');
    setError('');
  }, []);

  const handleSerialChange = (v) => {
    setSerial(v);
    if (serialError) setSerialError('');
    if (error) setError('');
  };

  const handleAssignExisting = useCallback(async () => {
    if (!duplicate) return;
    setSaving(true);
    setError('');
    const [ok, msg] = await onAssignExisting(duplicate);
    setSaving(false);
    if (ok) onClose(); else setError(msg || 'Could not assign that device.');
  }, [duplicate, onAssignExisting, onClose]);

  /* Target naming. Residents are "First Last" in a room; areas are the room
     itself, so the label/sub pair collapses to one line for areas. */
  const targetName = isAreaMode
    ? (target?.area_name || '')
    : `${target?.resident_first_name || ''} ${target?.resident_last_name || ''}`.trim();
  const targetSub = isAreaMode
    ? (target?.building_name || target?.area_type_name || '')
    : (target?.area_name || '');
  const targetFallback = isAreaMode ? 'this area' : 'this resident';
  const targetLabel = targetName || targetFallback;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const err = validateSerial(serial);
    if (err) { setSerialError(err); return; }
    if (duplicate) return;   // the duplicate panel takes over

    setSaving(true);
    setError('');
    const clean = String(parseInt(String(serial).trim(), 10));
    const formData = {
      campus_id: campusId,
      manufacture_id: picked.manufacture_id,
      manufacture_uuid: clean,
      device_name: picked.device_type_name || '',
      // Resolved rather than chosen — see the deviceUseId note above.
      device_use_id: deviceUseId,
      // Added through this flow the device is assigned and live immediately.
      is_active: 1,
      is_alerting_now: false,
      is_server: false,
      is_area_device: isAreaMode ? 1 : 0,
      is_resident: !isAreaMode,
      area_id: isAreaMode ? (target?.area_id || 0) : 0,
      // area_name only in area mode — the vendor packagers on that path expect
      // it, and the resident path has never sent it.
      ...(isAreaMode ? { area_name: target?.area_name || '' } : {}),
      resident_id: isAreaMode ? 0 : target?.resident_id,
      resident_area_id: isAreaMode ? 0 : (target?.area_id || 0),
      manufacture_model_id: model?.model_id ? parseInt(model.model_id, 10) : 0,
      escalation_enabled: true,
      descriptions: isAreaMode ? descriptions.trim() : '',
      device_type_id: parseInt(picked.device_type_id, 10),
    };
    const [ok, msg] = await onAdd(formData);
    setSaving(false);
    if (ok) onClose(); else setError(msg || 'Could not add that device.');
  }, [serial, duplicate, campusId, picked, model, deviceUseId, target, isAreaMode, descriptions, onAdd, onClose]);

  /* Dead-end escape hatch. The curated list is deliberately short, so a device
     that isn't on it is expected rather than exceptional — but only users who
     can actually drive the advanced Devices-page flow get pointed at it. */
  const NotSeeingIt = () => (
    <div className="eld-footnote">
      <p className="eld-footnote__title">Not seeing the device you&rsquo;re looking for?</p>
      <p className="eld-footnote__text">
        {roleWeight > ADVANCED_SETUP_MIN_ROLE ? (
          <>
            Try{' '}
            <Link
              to="/community/devices"
              state={{ openAddDevice: true, addDeviceBase: isAreaMode ? 'area' : 'resident' }}
              className="eld-footnote__link"
              onClick={onClose}
            >
              Advanced Device Setup
            </Link>
            , or contact your account manager to have it added.
          </>
        ) : (
          'Contact your account manager to have it added.'
        )}
      </p>
    </div>
  );

  const DeviceImage = ({ row, size }) => (
    row?.image_url ? (
      <img
        src={`/static/image/${row.image_url}`}
        alt=""
        aria-hidden="true"
        style={{ width: size, height: size, objectFit: 'contain' }}
        onError={(ev) => { ev.currentTarget.style.visibility = 'hidden'; }}
      />
    ) : (
      <span aria-hidden="true" style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)' }}>
        <i className="fe fe-cpu" style={{ fontSize: size * 0.5 }} />
      </span>
    )
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      classPrefix={p}
      size="md"
      ariaLabel={isAreaMode ? 'Add a new device to this area' : 'Add a new device to this resident'}
      title="Add New Device"
      rawBody
    >
      <form className={`${p}-form`} onSubmit={handleSubmit}>
        <div className={`${p}-form__body`}>
          <p className={`${p}-field__label`} style={{ marginBottom: 0 }}>
            For {targetLabel}{targetSub ? ` · ${targetSub}` : ''}
          </p>

          {/* ---------- Step 1: pick the device ---------- */}
          {step === 'device' ? (
            /* Mode-qualified help anchor (docs/INTERACTIVE_HELP_PLAN.md §5.1):
               this drawer serves four surfaces (resident/area × list/profile),
               so the ids carry the mode. The pendant flow references only
               `easy-add.resident.*` and therefore can never latch onto an Areas
               instance — no engine-side scoping needed. */
            <div data-help-id={`easy-add.${mode}.catalog`}>
              <p className={`${p}-field__label`}>What kind of device is it?</p>

              {catalogLoading ? (
                <p className={`${p}-cell-text--muted`}>Loading devices…</p>
              ) : catalog.length === 0 ? (
                <>
                  <p className={`${p}-cell-text--muted`}>No quick-add devices are configured.</p>
                  <NotSeeingIt />
                </>
              ) : (
                <>
                  <div className="eld-tiles">
                    {tiles.map((row) => (
                      <button
                        key={row.cloud_id}
                        type="button"
                        className="eld-tile"
                        onClick={() => handlePick(row)}
                      >
                        <DeviceImage row={row} size={56} />
                        <span className="eld-tile__name">{row.device_type_name}</span>
                        {row.device_category_short_name ? (
                          <span className="eld-tile__meta">{row.device_category_short_name}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>

                  {rest.length > 0 ? (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <p className={`${p}-field__label`}>Other devices</p>
                      <div className="eld-list">
                        {rest.map((row) => (
                          <button
                            key={row.cloud_id}
                            type="button"
                            className="eld-row"
                            onClick={() => handlePick(row)}
                          >
                            <DeviceImage row={row} size={24} />
                            <span className="eld-row__name">{row.device_type_name}</span>
                            {row.device_category_short_name ? (
                              <span className="eld-row__meta">{row.device_category_short_name}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <NotSeeingIt />
                </>
              )}
            </div>
          ) : null}

          {/* ---------- Step 2: identify it ---------- */}
          {step === 'serial' && picked ? (
            <>
              <div className="eld-picked">
                <DeviceImage row={picked} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className={`${p}-drawer-item__name`}>{picked.device_type_name}</p>
                  <p className={`${p}-drawer-item__meta ms-2`}>
                    {picked.manufacture_name}
                    {model?.manufacture_model_name || picked.manufacture_model_name
                      ? ` · ${model?.manufacture_model_name || picked.manufacture_model_name}`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="eld-link-btn"
                  onClick={() => { setListening(false); setStep('device'); }}
                >
                  Change
                </button>
              </div>

              <div>
                <label htmlFor="easy-serial" className={`${p}-field__label`}>
                  Serial number <span className={`${p}-field__required`}>*</span>
                </label>
                <input
                  id="easy-serial"
                  data-help-id={`easy-add.${mode}.serial`}
                  type="text"
                  inputMode="numeric"
                  value={serial}
                  onChange={(e) => handleSerialChange(e.target.value)}
                  placeholder="e.g. 123456"
                  className={`${p}-field__input ${serialError ? 'eld-invalid' : ''}`}
                  autoFocus
                />
                {serialError ? <p className="eld-error">{serialError}</p> : null}
                <p className="eld-hint">Printed on the device label.</p>
              </div>

              {/* Listen path */}
              {canListen ? (
                <div>
                  {!listening ? (
                    <button type="button" className={`${p}-btn ${p}-btn--secondary`} onClick={startListen}>
                      <i className="fe fe-radio" aria-hidden="true" style={{ marginRight: 6 }} />
                      Listen for device
                    </button>
                  ) : (
                    <>
                      <div className="eld-listen-bar">
                        <div
                          className="eld-listen-bar__fill"
                          style={{ width: `${(listenCount / LISTEN_SECONDS) * 100}%` }}
                        />
                      </div>
                      {/* Explicit flex row: the global `p { display: inline-block }`
                          in custom.css would otherwise butt this text straight
                          against the Stop button with no gap. */}
                      <div className="eld-listen-status">
                        <p className="eld-listen-status__text">
                          {heard.length > 0
                            ? `${heard.length} device${heard.length !== 1 ? 's' : ''} heard — ${LISTEN_SECONDS - listenCount}s left`
                            : `Press the button on the device… ${LISTEN_SECONDS - listenCount}s left`}
                        </p>
                        <button
                          type="button"
                          className="eld-link-btn"
                          onClick={() => setListening(false)}
                        >
                          Stop listening
                        </button>
                      </div>
                    </>
                  )}

                  {heard.length > 0 ? (
                    <div className="eld-list" style={{ marginTop: 'var(--space-2)' }}>
                      {heard.map((h) => (
                        <button
                          key={h.trx_id}
                          type="button"
                          className="eld-row"
                          onClick={() => { handleSerialChange(String(h.trx_id)); setListening(false); }}
                        >
                          <span className="eld-row__name">{h.name || 'Device'}</span>
                          <span className="eld-row__meta">#{h.trx_id}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Where in the room it sits. Area devices are identified by
                  placement rather than by a person, so this is what tells
                  staff which of three sensors in a suite just fired. Hidden
                  on the duplicate path — assigning an existing device keeps
                  the description it already has. Same label as the advanced
                  drawer's field, which writes the same column. */}
              {isAreaMode && !duplicate ? (
                <div>
                  <label htmlFor="easy-descriptions" className={`${p}-field__label`}>
                    Location within area
                  </label>
                  <input
                    id="easy-descriptions"
                    type="text"
                    value={descriptions}
                    onChange={(e) => setDescriptions(e.target.value)}
                    placeholder="Optional... Bedroom, Bathroom, etc."
                    className={`${p}-field__input`}
                  />
                </div>
              ) : null}

              {/* Duplicate serial → offer assignment instead of failing */}
              {duplicate ? (
                <div className="eld-dup">
                  <p className="eld-dup__title">This device is already in the system.</p>
                  <p className={`${p}-drawer-item__meta`} style={{ margin: '2px 0 8px' }}>
                    {duplicate.device_name || 'Device'} · #{duplicate.manufacture_uuid}
                    {duplicate.resident_id
                      ? ' · currently assigned to a resident'
                      : duplicate.is_area_device
                        ? ' · currently an area device'
                        : ' · currently unassigned'}
                  </p>
                  <button
                    type="button"
                    className={`${p}-btn ${p}-btn--primary`}
                    onClick={handleAssignExisting}
                    disabled={saving}
                  >
                    {saving ? 'Assigning…' : `Assign it to ${targetLabel}`}
                  </button>
                </div>
              ) : null}

              {/* Confirmation line — names the target before writing */}
              {!duplicate && serial.trim() && !serialError ? (
                <p className="eld-summary">
                  Adds {picked.device_type_name} #{String(serial).trim()} and assigns it to{' '}
                  {targetLabel}
                  {!isAreaMode && targetSub ? ` in ${targetSub}` : ''}
                  {isAreaMode && descriptions.trim() ? ` (${descriptions.trim()})` : ''}.
                </p>
              ) : null}

              {error ? <p className="eld-error">{error}</p> : null}
            </>
          ) : null}
        </div>

        <div className={`${p}-form__footer`}>
          <button type="button" onClick={onClose} className={`${p}-btn ${p}-btn--cancel`}>Cancel</button>
          {step === 'serial' ? (
            <button
              type="submit"
              className={`${p}-btn ${p}-btn--primary`}
              disabled={saving || !!duplicate || !serial.trim()}
            >
              {saving ? 'Adding…' : 'Add & Assign'}
            </button>
          ) : null}
        </div>
      </form>
    </Drawer>
  );
};

export default EasyAddDeviceDrawer;
